import * as LocalStorage from 'lib/localStorage'
import {
  AddLocalStorageSubNavPersonalizationItemParams,
  AddSubNavItemsFromArtworkAttributesParams,
  BuildSubNavItemParams,
  FilterSubNavByTypeAndLimitParams,
  GetHomepageSubNavItemsParams,
  GetLocalStorageSubNavPerUserParams,
  ReplaceSubNavByLocalStorageParams,
  SetLocalStorageSubNavPersonalizationParams,
  SetLocalStorageSubNavPerUserParams,
  SortSubNavItemsParams,
  SubNavPersonalizationItem,
  SubNavPersonalizationLocalStorageData,
  SubNavPersonalizationLocalStoragePerUserData,
  SubNavPersonalizationNoOrderItem,
} from '../types'
import {
  FACET_NAMES,
  ALLOWED_SUBJECT_VALUES,
  ALLOWED_STYLE_VALUES,
  ALLOWED_SIZE_VALUES,
  ALLOWED_CATEGORY_VALUES,
} from 'lib/constructor/constants/hardcodedSaatchiFilters'
import { capitalizeString } from 'lib/helpers'
import { slugify } from 'lib/slugify'

const LOCAL_STORAGE_SUB_NAV_PERSONALIZATION_KEY = 'saatchi_sub_nav_personalization'
const MAX_SUB_NAV_ITEMS_IN_LOCAL_STORAGE = 8
const MAX_SUB_NAV_CATEGORY_ITEMS = 3
const MAX_SUB_NAV_STYLE_ITEMS = 3
const MAX_SUB_NAV_SUBJECT_ITEMS = 3
const MAX_SUB_NAV_SIZE_ITEMS = 1

const CATEGORY_MAPPING = {
  painting: 'paintings',
  drawing: 'drawings',
}

const DEFAULT_ARTWORK_ATTRIBUTES_LIST = ['category', 'styles', 'subject', 'size', 'size_bin']

export const SUB_NAV_PERSONALIZATION_TYPES = {
  CATEGORY: 'category',
  STYLE: 'style',
  SUBJECT: 'subject',
  SIZE: 'size',
}

export const FACETS_MAPPING_TO_NAVIGATION_TYPE = {
  [FACET_NAMES.CATEGORY]: SUB_NAV_PERSONALIZATION_TYPES.CATEGORY,
  [FACET_NAMES.STYLES]: SUB_NAV_PERSONALIZATION_TYPES.STYLE,
  [FACET_NAMES.SUBJECT]: SUB_NAV_PERSONALIZATION_TYPES.SUBJECT,
  [FACET_NAMES.SIZE]: SUB_NAV_PERSONALIZATION_TYPES.SIZE,
  [SUB_NAV_PERSONALIZATION_TYPES.SIZE]: SUB_NAV_PERSONALIZATION_TYPES.SIZE,
}

const subNavCategories: ReadonlyArray<SubNavPersonalizationNoOrderItem> = [
  {
    type: SUB_NAV_PERSONALIZATION_TYPES.CATEGORY,
    label: 'Shop All',
    url: '/all',
    value: 'all',
  },
  {
    type: SUB_NAV_PERSONALIZATION_TYPES.CATEGORY,
    label: 'Paintings',
    url: '/paintings',
    value: 'paintings',
  },
  {
    type: SUB_NAV_PERSONALIZATION_TYPES.CATEGORY,
    label: 'Sculptures',
    url: '/sculpture',
    value: 'sculpture',
  },
]

const subNavStyles: ReadonlyArray<SubNavPersonalizationNoOrderItem> = [
  {
    type: SUB_NAV_PERSONALIZATION_TYPES.STYLE,
    label: 'Abstracts',
    url: '/all/abstract',
    value: 'abstract',
  },
  {
    type: SUB_NAV_PERSONALIZATION_TYPES.STYLE,
    label: 'Figurative',
    url: '/all/figurative',
    value: 'figurative',
  },
  {
    type: SUB_NAV_PERSONALIZATION_TYPES.STYLE,
    label: 'Expressionism',
    url: '/all/expressionism',
    value: 'expressionism',
  },
]

const subNavSubjects: ReadonlyArray<SubNavPersonalizationNoOrderItem> = [
  {
    type: SUB_NAV_PERSONALIZATION_TYPES.SUBJECT,
    label: 'Landscapes',
    url: '/all/landscape',
    value: 'landscape',
  },
  {
    type: SUB_NAV_PERSONALIZATION_TYPES.SUBJECT,
    label: 'Flowers',
    url: '/all/floral',
    value: 'floral',
  },
  {
    type: SUB_NAV_PERSONALIZATION_TYPES.SUBJECT,
    label: 'People',
    url: '/all/people',
    value: 'people',
  },
]

const subNavSizes: ReadonlyArray<SubNavPersonalizationNoOrderItem> = [
  {
    type: SUB_NAV_PERSONALIZATION_TYPES.SIZE,
    label: 'Large Works',
    url: '/all/large',
    value: 'large',
  },
]

const subNavOrder = [
  SUB_NAV_PERSONALIZATION_TYPES.CATEGORY,
  SUB_NAV_PERSONALIZATION_TYPES.CATEGORY,
  SUB_NAV_PERSONALIZATION_TYPES.STYLE,
  SUB_NAV_PERSONALIZATION_TYPES.SIZE,
  SUB_NAV_PERSONALIZATION_TYPES.SUBJECT,
  SUB_NAV_PERSONALIZATION_TYPES.CATEGORY,
  SUB_NAV_PERSONALIZATION_TYPES.STYLE,
  SUB_NAV_PERSONALIZATION_TYPES.STYLE,
  SUB_NAV_PERSONALIZATION_TYPES.SUBJECT,
  SUB_NAV_PERSONALIZATION_TYPES.SUBJECT,
]

const defaultHardcodedWhitelistSubNav = [
  ...ALLOWED_CATEGORY_VALUES,
  { label: 'Shop All', value: 'all' }, // adding All to work with constructor and algolia
  { label: 'Mixed Media', value: 'mixed media' }, // adding mixed media with space in the value to work with constructor and algolia
  { label: 'Prints', value: 'prints' }, // adding prints to work with constructor and algolia
  ...ALLOWED_SUBJECT_VALUES,
  ...ALLOWED_STYLE_VALUES,
  ...ALLOWED_SIZE_VALUES,
]

const sortSubNavItems = ({
  subNavItems,
  subNavOrderByType,
}: SortSubNavItemsParams): ReadonlyArray<SubNavPersonalizationItem> => {
  const subNavGroupedByType = subNavItems.reduce((subNavGroupsByType, item) => {
    if (!subNavGroupsByType[item.type]) {
      subNavGroupsByType[item.type] = []
    }

    subNavGroupsByType[item.type].push(item)
    return subNavGroupsByType
  }, [])

  const subNavItemsOrdered = subNavOrderByType.reduce((sortedSubNavItems, type, index) => {
    const subNavGroupByType = subNavGroupedByType[type]

    if (!subNavGroupByType || subNavGroupByType.length === 0) {
      return sortedSubNavItems
    }

    const subNavItem = subNavGroupByType.shift()

    return [
      ...sortedSubNavItems,
      {
        order: index,
        ...subNavItem,
      },
    ]
  }, [] as ReadonlyArray<SubNavPersonalizationItem>)

  return subNavItemsOrdered
}

const getLocalStorageSubNavPersonalization = (): SubNavPersonalizationLocalStorageData => {
  return LocalStorage.get(LOCAL_STORAGE_SUB_NAV_PERSONALIZATION_KEY)
}

const getLocalStorageSubNavPerUser = ({
  userId,
}: GetLocalStorageSubNavPerUserParams): SubNavPersonalizationLocalStoragePerUserData => {
  const localStorageSubNavItems = LocalStorage.get(LOCAL_STORAGE_SUB_NAV_PERSONALIZATION_KEY)
  return localStorageSubNavItems?.[userId] || {}
}

export const setLocalStorageSubNavPersonalization = ({
  newSubNavPersonalizationData,
}: SetLocalStorageSubNavPersonalizationParams) => {
  LocalStorage.set(LOCAL_STORAGE_SUB_NAV_PERSONALIZATION_KEY, newSubNavPersonalizationData)
}

export const setLocalStorageSubNavPerUser = ({
  newSubNavPersonalizationData,
  userId,
}: SetLocalStorageSubNavPerUserParams) => {
  const localStorageSubNavAllData: SubNavPersonalizationLocalStorageData = getLocalStorageSubNavPersonalization()
  const allSubNavPersonalizationItems = {
    ...localStorageSubNavAllData,
    [userId]: {
      ...(localStorageSubNavAllData?.[userId] || {}),
      ...newSubNavPersonalizationData,
    },
  }
  setLocalStorageSubNavPersonalization({
    newSubNavPersonalizationData: allSubNavPersonalizationItems,
  })
}

export const addLocalStorageSubNavPersonalizationItem = ({
  newItem,
  type,
  userId,
  shouldUseWhitelist = true,
  hardcodedWhitelistSubNav = defaultHardcodedWhitelistSubNav,
}: AddLocalStorageSubNavPersonalizationItemParams) => {
  const isNewItemInWhitelist = hardcodedWhitelistSubNav.some(
    ({ value }) => value === newItem?.value
  )
  if (shouldUseWhitelist && !isNewItemInWhitelist) {
    return
  }

  const localStorageSubNavData = getLocalStorageSubNavPerUser({ userId })
  const filteredLocalStorageSubNav = Object.entries(localStorageSubNavData).reduce(
    (localStorageAccumulator, [facetName, facetValues]) => {
      localStorageAccumulator[facetName] = facetValues.filter(({ url }) => url !== newItem?.url)
      return localStorageAccumulator
    },
    {} as SubNavPersonalizationLocalStoragePerUserData
  )

  const subNavItemsByType = Array.isArray(filteredLocalStorageSubNav?.[type])
    ? filteredLocalStorageSubNav[type]
    : []
  const newSubNavItems: ReadonlyArray<SubNavPersonalizationNoOrderItem> = [
    newItem,
    ...subNavItemsByType,
  ]

  const filteredSubNavItems = newSubNavItems.slice(0, MAX_SUB_NAV_ITEMS_IN_LOCAL_STORAGE)
  const newSubNavPersonalizationLocalStorageData: SubNavPersonalizationLocalStoragePerUserData = {
    ...filteredLocalStorageSubNav,
    [type]: filteredSubNavItems,
  }

  setLocalStorageSubNavPerUser({
    newSubNavPersonalizationData: newSubNavPersonalizationLocalStorageData,
    userId,
  })
}

export const addSubNavItemsFromArtworkAttributes = ({
  artwork,
  userId,
  artworkAttributesList = DEFAULT_ARTWORK_ATTRIBUTES_LIST,
}: AddSubNavItemsFromArtworkAttributesParams) => {
  for (const attributeName of artworkAttributesList) {
    const itemValue = Array.isArray(artwork?.[attributeName])
      ? artwork?.[attributeName]?.[0]
      : artwork?.[attributeName]

    addLocalStorageSubNavPersonalizationItem({
      newItem: buildSubNavItem({
        type: attributeName,
        itemValue: itemValue?.toLowerCase(),
      }),
      type: FACETS_MAPPING_TO_NAVIGATION_TYPE[attributeName],
      userId,
    })
  }
}

const filterSubNavByTypeAndLimit = ({
  localStorageSubNavData,
  type,
  defaultItemsByType,
  maxNumberOfItems,
  previousMergedItems = [], // to remove an item if the url already exists in previous types
}: FilterSubNavByTypeAndLimitParams) => {
  const localStorageSubNavByType = localStorageSubNavData?.[type] || []
  const filteredSubNavItemByPreviousItems = localStorageSubNavByType.filter(
    ({ url }) => !previousMergedItems.some(({ url: previousItemUrl }) => url === previousItemUrl)
  )
  const mergedLocalStorageAndDefaultSubNavItems = [
    ...filteredSubNavItemByPreviousItems,
    ...defaultItemsByType,
  ]
  const dedupedSubNavItems = Array.from(
    new Map(mergedLocalStorageAndDefaultSubNavItems.map((item) => [item.url, item])).values()
  )

  return dedupedSubNavItems.slice(0, maxNumberOfItems)
}

const replaceSubNavByLocalStorage = ({
  userId,
}: ReplaceSubNavByLocalStorageParams): ReadonlyArray<SubNavPersonalizationNoOrderItem> => {
  const localStorageSubNav = getLocalStorageSubNavPerUser({ userId })
  const subNavSizeItems = filterSubNavByTypeAndLimit({
    localStorageSubNavData: localStorageSubNav,
    type: SUB_NAV_PERSONALIZATION_TYPES.SIZE,
    defaultItemsByType: subNavSizes.map((sizeObject) => ({
      ...sizeObject,
      url: '/all/large',
    })),
    maxNumberOfItems: MAX_SUB_NAV_SIZE_ITEMS,
  })
  const subNavCategoryItems = filterSubNavByTypeAndLimit({
    localStorageSubNavData: localStorageSubNav,
    type: SUB_NAV_PERSONALIZATION_TYPES.CATEGORY,
    defaultItemsByType: subNavCategories,
    maxNumberOfItems: MAX_SUB_NAV_CATEGORY_ITEMS,
  })
  const subNavStyleItems = filterSubNavByTypeAndLimit({
    localStorageSubNavData: localStorageSubNav,
    type: SUB_NAV_PERSONALIZATION_TYPES.STYLE,
    defaultItemsByType: subNavStyles,
    maxNumberOfItems: MAX_SUB_NAV_STYLE_ITEMS,
  })
  const subNavSubjectItems = filterSubNavByTypeAndLimit({
    localStorageSubNavData: localStorageSubNav,
    type: SUB_NAV_PERSONALIZATION_TYPES.SUBJECT,
    defaultItemsByType: subNavSubjects,
    maxNumberOfItems: MAX_SUB_NAV_SUBJECT_ITEMS,
    previousMergedItems: subNavStyleItems,
  })

  return [...subNavCategoryItems, ...subNavStyleItems, ...subNavSubjectItems, ...subNavSizeItems]
}

export const buildSubNavItem = ({
  type,
  itemValue,
}: BuildSubNavItemParams): SubNavPersonalizationNoOrderItem => {
  let subNavItemLabel = capitalizeString(itemValue)
  let subNavItemUrl = ''
  let newItemValue = itemValue
  switch (type) {
    case FACET_NAMES.CATEGORY:
      const categoryValue = CATEGORY_MAPPING[itemValue?.toLowerCase()] || itemValue
      newItemValue = categoryValue
      subNavItemUrl = `/${slugify(categoryValue)}`
      subNavItemLabel = capitalizeString(categoryValue)
      break

    case FACET_NAMES.STYLES:
    case FACET_NAMES.SUBJECT:
      subNavItemUrl = `/all/${slugify(itemValue)}`
      break

    case FACET_NAMES.SIZE:
    case SUB_NAV_PERSONALIZATION_TYPES.SIZE:
      subNavItemUrl = `/all/${itemValue}`
      subNavItemLabel = `${capitalizeString(itemValue)} Works`
      break

    default:
      subNavItemUrl = 'all'
      break
  }

  return {
    type: FACETS_MAPPING_TO_NAVIGATION_TYPE[type],
    label: subNavItemLabel,
    url: subNavItemUrl,
    value: newItemValue,
  }
}

export const getHomepageSubNavItems = ({
  userId,
}: GetHomepageSubNavItemsParams): ReadonlyArray<SubNavPersonalizationItem> => {
  const subNavItemsToSort = replaceSubNavByLocalStorage({ userId })

  return sortSubNavItems({
    subNavItems: subNavItemsToSort,
    subNavOrderByType: subNavOrder,
  })
}
